import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { getLanguage } from "@/utils/languageUtils";
import { vehicleCardUILabels } from "@/i18n";
import { useState, useEffect } from "react";
import { ValueEstimation } from "@/types/vehicleCard";

interface ValueEstimationSectionProps {
  valueData?: any;
  priceCategories?: any;
  onRefreshPrices: () => Promise<void>;
  isUpdating: boolean;
  emptyMessage?: string;
}

export const ValueEstimationSection = ({
  valueData,
  priceCategories: propPriceCategories,
  onRefreshPrices,
  isUpdating,
  emptyMessage,
}: ValueEstimationSectionProps) => {
  const vcLabels = vehicleCardUILabels(getLanguage());
  const defaultEmptyMessage = emptyMessage ?? vcLabels.noValueEstimationData;
  const [priceCategories, setPriceCategories] = useState(
    propPriceCategories || [],
  );

  // Update local state when prop changes
  useEffect(() => {
    setPriceCategories(propPriceCategories || []);
  }, [propPriceCategories]);

  // Safety check - don't render if no data
  if (!propPriceCategories || propPriceCategories.length === 0) {
    return (
      <Card className="backdrop-blur-md">
        <CardContent className="flex flex-col gap-4 p-4 sm:p-6">
          <div className="flex shrink-0 justify-end">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 hover:bg-[#9b87f5]/5"
              onClick={onRefreshPrices}
              disabled={isUpdating}
            >
              <RefreshCw
                className={`h-4 w-4 ${isUpdating ? "animate-spin" : ""}`}
              />
              {isUpdating ? vcLabels.updating : vcLabels.updatePrices}
            </Button>
          </div>
          <p className="min-w-0 text-center text-sm text-muted-foreground sm:text-left">
            {defaultEmptyMessage}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardContent className="p-4 relative">
        <div className="flex justify-end mb-4">
          <Button
            variant="outline"
            size="sm"
            className="gap-2 hover:bg-[#9b87f5]/5"
            onClick={onRefreshPrices}
            disabled={isUpdating}
          >
            <RefreshCw
              className={`h-4 w-4 ${isUpdating ? "animate-spin" : ""}`}
            />
            {isUpdating ? vcLabels.updating : vcLabels.updatePrices}
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-5">
          {[...priceCategories].reverse().map((category, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center space-y-1.5"
            >
              <div
                className="text-base font-semibold"
                style={{
                  color:
                    category.color === "success"
                      ? "#16a34a"
                      : category.color === "info"
                        ? "#0284c7"
                        : category.color === "warning"
                          ? "#ca8a04"
                          : category.color === "destructive"
                            ? "#dc2626"
                            : "#666666",
                }}
              >
                {category.label}
              </div>
              <div className="font-medium text-sm">
                {category.lowerBound.toLocaleString("sv-SE")} -{" "}
                {category.upperBound.toLocaleString("sv-SE")} kr
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
